import { PointTooltipProps, ResponsiveLine, Serie } from '@nivo/line';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { commonThemeConfig } from '../../common/utils/chartConfig';
import { getDimensionColor } from '../store/workforce.helper';

export type LineChartProps = {
  data: (Serie & { yAxis?: 'left' | 'right' })[];
  xTicks?: number[];
  xTickFormat?: (tick: number) => string;
  yScales?: {
    left: any;
    right?: any;
  };
  tooltip?: (props: PointTooltipProps) => React.ReactElement;
  sliceTooltip?: (props: any) => React.ReactElement;
  yFormat?: string;
  axisLeft?: any;
  axisRight?: any;
};

const LineChart: React.FC<LineChartProps> = props => {
  const { t: tMes } = useTranslation('measures');
  const { t } = useTranslation('simulation');

  // Determine if any series uses right axis
  const hasRight = props.data.some(d => d.yAxis === 'right');

  // Check if we have negative values
  const hasNegativeValues = props.data.some(serie =>
    serie.data.some(point => (point.y as number) < 0),
  );

  // Assign yScaleId based on yAxis prop
  const chartData = props.data.map(d => ({
    ...d,
    yScaleId: d.yAxis === 'right' ? 'right' : 'left',
  }));

  const chartSideMargin = 60;
  const chartRightMargin = hasRight ? 60 : 20;

  return (
    <ResponsiveLine
      margin={{
        top: 20,
        right: chartRightMargin,
        bottom: 60,
        left: chartSideMargin,
      }}
      data={chartData}
      curve="monotoneX"
      enablePoints={false}
      pointLabelYOffset={0}
      enablePointLabel={false}
      colors={d => {
        return getDimensionColor(d.id ?? d.type)[0];
      }}
      xScale={{
        type: 'linear',
        nice: true,
      }}
      yScale={
        props.yScales?.left || {
          type: 'linear',
          nice: true,
          min: 'auto',
          max: 'auto',
        }
      }
      yFormat={props.yFormat}
      axisLeft={
        props.axisLeft ?? {
          tickRotation: 0,
          tickPadding: 5,
          tickSize: 5,
          legend: t`% / Rate`,
          legendOffset: -45,
          legendPosition: 'middle',
          format: v => `${v.toFixed(2)}%`,
        }
      }
      axisRight={
        props.axisRight ??
        (hasRight
          ? {
              tickRotation: 0,
              tickPadding: 5,
              tickSize: 5,
              legend: t('Moves'),
              legendOffset: 45,
              legendPosition: 'middle',
              format: v => `${v}`,
            }
          : null)
      }
      axisBottom={{
        ticksPosition: 'before',
        tickValues: props.xTicks,
        tickPadding: 5,
        tickRotation: 0,
        tickSize: 5,
        legend: t`Time / Total Steps`,
        legendOffset: 40,
        legendPosition: 'middle',
        format: props.xTickFormat,
      }}
      theme={commonThemeConfig}
      isInteractive={true}
      tooltip={props.tooltip}
      sliceTooltip={props.sliceTooltip}
      enableSlices={props.sliceTooltip ? 'x' : false}
      useMesh={true}
      debugSlices={false}
      enableCrosshair={true}
      crosshairType="x"
      markers={
        hasNegativeValues
          ? [
              {
                axis: 'y',
                value: 0,
                lineStyle: {
                  stroke: 'rgba(255, 255, 255, 0.8)',
                  strokeWidth: 1,
                  strokeDasharray: '1 2',
                },
                // legend: 'Zero Line',
                legendOrientation: 'horizontal',
              },
            ]
          : []
      }
      layers={[
        'grid',
        'markers',
        'lines',
        'slices',
        'crosshair',
        'mesh',
        'axes',
        'points',
        'legends',
      ]}
    />
  );
};

export default LineChart;
